import { useMemo, useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { deleteAccountMember, fetchAccountMembers, updateAccountMemberRole } from 'api/account';
import { CopyButton } from 'components/copy-button';
import * as DropdownMenu from 'components/dropdown-menu';
import { Permission } from 'components/permissions/Permission';
import { usePermissions } from 'components/permissions/usePermissions';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMember } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const isOnlyAdministrator = (permitted: boolean, members: AccountMember[]) => {
    return permitted && members.filter((user: AccountMember) => user.role === 'admin').length === 1;
};

const columnHelper = createColumnHelper<AccountMember>();

const AccountMembers = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { pagination, setPagination } = Table.useReactTableState();
    const { isPermitted } = usePermissions([Permission.Admin]);

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data: members } = useSuspenseQuery({
        queryKey: accountQueryKeys.membersWithFilters(accountId, params),
        queryFn: () => fetchAccountMembers(accountId, params),
        refetchOnMount: 'always',
    });

    const { mutate: deleteMember, isPending: isDeleting } = useMutation<
        void,
        GivebutterAxiosError,
        { id: number }
    >({
        mutationFn: ({ id }) => deleteAccountMember(accountId, id),
        onSuccess: () => {
            toast.success('Deleted member successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.members(accountId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete member');
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: 'Contact',
                cell: info => {
                    const member = info.row.original;

                    return (
                        <div className="flex items-center gap-2">
                            <Avatar.Root size="xs">
                                <Avatar.Image
                                    alt={`Profile picture of ${member.first_name} ${member.last_name}`}
                                    src={member.picture}
                                />
                                <Avatar.Fallback
                                    aria-label={`${member.first_name} ${member.last_name}`}
                                >
                                    {member.first_name?.[0]}
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <div className="flex flex-col">
                                <p className="font-medium">
                                    {member.first_name} {member.last_name}
                                </p>
                                <div className="flex items-center gap-1">
                                    <a href={`mailto:${member.email}`} className="text-sm">
                                        {member.email}
                                    </a>
                                    <CopyButton text={member.email} />
                                </div>
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('role', {
                header: 'Role',
                cell: info => {
                    const member = info.row.original;
                    const isOnlyAdmin = isOnlyAdministrator(isPermitted, members.data);
                    const isDisabled = isOnlyAdmin && member.role === 'admin';

                    return <AccountMemberRoleSwitcher member={member} disabled={isDisabled} />;
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const member = info.row.original;
                    const isOnlyAdmin = isOnlyAdministrator(isPermitted, members.data);

                    const isDisabled = isOnlyAdmin && member.role === 'admin';

                    return (
                        <DropdownMenu.Root key={member.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                <AlertDialog.Root type="error">
                                    <AlertDialog.Trigger type="reset" asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            disabled={isDisabled || isDeleting}
                                        >
                                            Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>Delete user</AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You will not be able to undo this action.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => {
                                                        if (!isDisabled) {
                                                            deleteMember({ id: member.id });
                                                        }
                                                    }}
                                                    disabled={isDisabled || isDeleting}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [deleteMember, isDeleting, isPermitted, members.data],
    );

    const table = useReactTable({
        data: members.data,
        columns,
        pageCount: members.meta.last_page,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, members);

    return (
        <Box className="overflow-hidden">
            <Table.Root isLoading={isPending} variant="bordered" table={table}>
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon />
                    <Table.EmptyTitle>No account members found</Table.EmptyTitle>
                </Table.Empty>
                <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </Box>
    );
};

interface AccountMemberRoleSwitcherProps {
    member: AccountMember;
    disabled?: boolean;
}
const AccountMemberRoleSwitcher = ({ member, disabled }: AccountMemberRoleSwitcherProps) => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const [value, setValue] = useState(member.role);

    const { mutate: changeMemberRole, isPending: isChangingRole } = useMutation<
        void,
        GivebutterAxiosError,
        { id: number; role: string }
    >({
        mutationFn: ({ id, role }) => updateAccountMemberRole(accountId, id, role),
        onSuccess: () => {
            toast.success('Updated user role successfully!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.members(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to update user role');
        },
    });

    return (
        <div className="flex items-center">
            <SingleSelect.Root
                value={value}
                onValueChange={value => {
                    setValue(value);
                    changeMemberRole({ id: member.id, role: value });
                }}
            >
                <SingleSelect.Trigger
                    aria-label="Member role"
                    disabled={disabled || isChangingRole}
                >
                    <SingleSelect.Value placeholder="Select a role" />
                </SingleSelect.Trigger>
                <SingleSelect.Content>
                    <SingleSelect.Item value="editor">
                        <SingleSelect.ItemText>Editor</SingleSelect.ItemText>
                    </SingleSelect.Item>
                    <SingleSelect.Item value="admin">
                        <SingleSelect.ItemText>Admin</SingleSelect.ItemText>
                    </SingleSelect.Item>
                </SingleSelect.Content>
            </SingleSelect.Root>
        </div>
    );
};

export { AccountMembers };
